import type {
  ColumnFilter,
  FilterSpec,
} from "@tsmono/inspect-components/columnFilter";

import type {
  FilterVarMapping,
  SampleFilterSpecRegistry,
} from "./filterSpecRegistry";

// Filtrex's string syntax only recognizes two escapes (`\"` and `\\`),
// so `\.` / `\+` / etc. are *invalid* and would be rejected by the
// filtrex lexer. To match a literal regex metachar inside a filtrex
// string, we emit a single-element character class (`[.]`, `[+]`, …).
// Three metachars can't use that scheme and get a backslash escape
// instead (which survives filtrex because `stringLiteral` doubles the
// backslash into the valid `\\` escape): `^` (`[^]` matches ANY char),
// `]` (`[]]` never matches), and `\` itself.
const regexEscape = (s: string): string =>
  s.replace(/[\\^\]]/g, "\\$&").replace(/[.*+?${}()|[]/g, "[$&]");

// Filtrex's lexer recognizes only two string escapes: `\"` and `\\`.
// JSON.stringify additionally encodes control chars (`\n`, `\t`, …) which
// filtrex would reject, so we use a minimal escape that produces
// strings the parser can round-trip.
const stringLiteral = (s: string): string => `"${s.replace(/[\\"]/g, "\\$&")}"`;

const numberLiteral = (n: number): string => String(n);

/** Convert a single per-column filter spec into a filtrex predicate
 *  (string), or `null` if we can't represent it. */
const specToFragment = (
  mapping: FilterVarMapping,
  spec: FilterSpec
): string | null => {
  const v = mapping.variable;
  const op = spec.operator;

  // `None` is filtrex's null sentinel (registered in
  // `filterExpressionConstants` in filters.ts) — emitting `null` would
  // resolve as an unbound identifier and error.
  if (op === "is blank") return `${v} == None`;
  if (op === "is not blank") return `${v} != None`;

  // Number-style operators.
  if (mapping.kind === "number") {
    // `Number("")` is 0, which would silently synthesize `var == 0`;
    // reject empty/whitespace values like every other malformed input.
    if (spec.value.trim() === "") return null;
    const n = Number(spec.value);
    // Reject unparseable / non-finite raw values (NaN, Infinity) —
    // `var == NaN` is invalid filtrex.
    if (!Number.isFinite(n)) return null;
    switch (op) {
      case "=":
        return `${v} == ${numberLiteral(n)}`;
      case "!=":
        return `${v} != ${numberLiteral(n)}`;
      case "<":
        return `${v} < ${numberLiteral(n)}`;
      case "<=":
        return `${v} <= ${numberLiteral(n)}`;
      case ">":
        return `${v} > ${numberLiteral(n)}`;
      case ">=":
        return `${v} >= ${numberLiteral(n)}`;
      case "between": {
        if ((spec.value2 ?? "").trim() === "") return null;
        const n2 = Number(spec.value2);
        if (!Number.isFinite(n2)) return null;
        return `(${v} >= ${numberLiteral(n)} and ${v} <= ${numberLiteral(n2)})`;
      }
      default:
        return null;
    }
  }

  // String column.
  const f = spec.value;
  switch (op) {
    case "=":
      return `${v} == ${stringLiteral(f)}`;
    case "!=":
      return `${v} != ${stringLiteral(f)}`;
    case "contains":
      return mapping.containsFn
        ? `${mapping.containsFn}(${stringLiteral(regexEscape(f))})`
        : `${v} ~= ${stringLiteral(regexEscape(f))}`;
    case "does not contain":
      return mapping.containsFn
        ? `not ${mapping.containsFn}(${stringLiteral(regexEscape(f))})`
        : `not (${v} ~= ${stringLiteral(regexEscape(f))})`;
    case "starts with":
      return `${v} ~= ${stringLiteral("^" + regexEscape(f))}`;
    case "ends with":
      return `${v} ~= ${stringLiteral(regexEscape(f) + "$")}`;
    default:
      return null;
  }
};

/**
 * Render per-column filter specs as a filtrex expression.
 *
 * Returns `""` when there are no specs (caller clears the text), `null` when
 * any spec is not representable (caller must leave the text alone — a partial
 * string would be recognized back into a subset and wipe the rest), otherwise
 * the ` and `-joined expression.
 */
export function specsToFilterText(
  specs: Record<string, ColumnFilter>,
  registry: SampleFilterSpecRegistry
): string | null {
  if (Object.keys(specs).length === 0) return "";
  const parts: string[] = [];
  for (const [colId, filter] of Object.entries(specs)) {
    const mapping = registry.byColId.get(colId);
    if (!mapping) return null;
    const spec = filter.spec;
    const frag1 = specToFragment(mapping, spec);
    if (frag1 === null) return null;
    if (spec.join && spec.second) {
      const frag2 = specToFragment(mapping, spec.second);
      if (frag2 === null) return null;
      parts.push(`(${frag1} ${spec.join} ${frag2})`);
    } else {
      parts.push(frag1);
    }
  }
  return parts.join(" and ");
}
